"use client";

import React from "react";
import { Clock, Wrench, ShieldAlert, Handshake, CircleCheck as CheckCircle2, ArrowRight, PhoneCall } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
interface AdvantageItem {
  id: string;
  title: string;
  description: string;
  iconName: "clock" | "wrench" | "shield" | "handshake";
  badge: string;
}
const advantages: AdvantageItem[] = [{
  id: "schedule",
  title: "Strict schedule adherence",
  description: "We erect and inspect platforms before your roofing crew arrives on site, so your strip-out and tiling begin without waiting.",
  iconName: "clock",
  badge: "Reliable Handover"
}, {
  id: "modifications",
  title: "Fast site alterations",
  description: "Need hop-ups moved or loading bays adjusted mid-job? Our team responds quickly without stalling your programme.",
  iconName: "wrench",
  badge: "Responsive Adjustments"
}, {
  id: "weatherproofing",
  title: "Rapid weather-proofing",
  description: "When forecasts turn, we supply temporary roofs, debris netting, and monarflex sheeting to protect open roof timbers.",
  iconName: "shield",
  badge: "Weather Protection"
}, {
  id: "partnership",
  title: "Direct trade accounts",
  description: "Straightforward contractor rates, predictable hire periods, and direct line contact with Pete Wood and operational supervisors.",
  iconName: "handshake",
  badge: "Trade Pricing"
}];
const accreditationHighlights = ["NASC verified safety standards", "CHAS accredited working practices", "Over 20 years frontline trade experience"];
function AdvantageIcon({
  name
}: {
  name: AdvantageItem["iconName"];
}) {
  const iconProps = {
    className: "size-5 text-primary"
  };
  switch (name) {
    case "clock":
      return <Clock {...iconProps} />;
    case "wrench":
      return <Wrench {...iconProps} />;
    case "shield":
      return <ShieldAlert {...iconProps} />;
    case "handshake":
      return <Handshake {...iconProps} />;
    default:
      return <CheckCircle2 {...iconProps} />;
  }
}
function ContractorsContent() {
  const prefersReducedMotion = useReducedMotion();
  const {
    posts,
    loading
  } = usePosts();
  const targetPhoto = posts?.find((p: any) => p?.acf?.photo_name === "Roofing Access Image" || p?.customFields?.photo_name === "Roofing Access Image");
  const rawImage = targetPhoto?.acf?.image || targetPhoto?.customFields?.image;
  const imageSrc = typeof rawImage === "string" && rawImage.trim() !== "" ? rawImage : typeof rawImage === "object" && rawImage?.url ? rawImage.url : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/roofing_scaffold_work_platform.png";
  const rawAlt = targetPhoto?.acf?.alt_text || targetPhoto?.customFields?.alt_text;
  const imageAlt = typeof rawAlt === "string" && rawAlt.trim() !== "" ? rawAlt : "Roofing contractor team working safely on a secured BMF scaffolding platform";
  const isFallback = imageSrc === "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/roofing_scaffold_work_platform.png";
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="why-roofing-contractors-choose-bmf" data-nav="light" className="bg-background text-foreground relative border-b border-border py-16 md:py-24 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 md:px-8 lg:px-12">
        {/* Section Header */}
        <div className="max-w-3xl">
          <div className="bg-primary mb-4 h-1 w-12 rounded-lg" />
          <p className="text-muted-foreground text-xs font-bold uppercase tracking-wider">
            Trade partnerships & access solutions
          </p>
          <h2 className="font-default text-foreground mt-2 text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
            Why roofing contractors choose BMF
          </h2>
          <p className="text-muted-foreground mt-4 text-base leading-relaxed md:text-lg">
            Re-roofing projects cannot afford delayed handovers or unresponsive
            scaffolders when weather closes in. We keep your strip-out, timber
            repairs, and slating crews moving safely across the South East.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          {/* Left Column: 4 Key Advantages */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-7">
            {advantages.map((item, i) => <motion.div key={item.id} variants={itemVariants} data-index={i} className="h-full">
                <Card className="border-border bg-card hover:border-primary/50 flex h-full flex-col justify-between rounded-lg border p-6 shadow-xs transition-[border-color,box-shadow] duration-200 hover:shadow-md">
                  <div>
                    <CardHeader className="p-0">
                      <div className="flex items-center justify-between gap-3">
                        <div className="bg-primary/10 flex size-10 items-center justify-center rounded-md">
                          <AdvantageIcon name={item.iconName} />
                        </div>
                        <span className="bg-muted text-muted-foreground rounded px-2.5 py-1 text-xs font-semibold">
                          {item.badge}
                        </span>
                      </div>
                      <CardTitle className="font-default text-foreground mt-4 text-lg font-bold">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 pt-2.5">
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                        {item.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  <div className="border-border/60 mt-5 border-t pt-4">
                    <span className="text-foreground inline-flex items-center gap-1.5 text-xs font-semibold">
                      <CheckCircle2 className="text-primary size-3.5" />
                      Safety-led access on every site
                    </span>
                  </div>
                </Card>
              </motion.div>)}
          </motion.div>

          {/* Right Column: Documentary Photography & Proof Block */}
          <motion.div initial={{
          opacity: 0,
          y: prefersReducedMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col gap-6 lg:col-span-5">
            {/* Visual Case Shot */}
            <div className="border-border bg-card overflow-hidden rounded-lg border shadow-xs">
              <div className="relative">
                <AspectRatio ratio={16 / 10}>
                  {loading ? <div className="h-full w-full bg-muted animate-pulse" /> : <img src={imageSrc} data-wvc-srcset={isFallback ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/roofing_scaffold_work_platform.png-wvc-srcset" : ""} data-wvc-sizes={isFallback ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/roofing_scaffold_work_platform.png-wvc-sizes" : ""} loading="lazy" decoding="async" alt={imageAlt} className="h-full w-full object-cover" />}
                </AspectRatio>
                <div className="bg-secondary/90 text-secondary-foreground absolute bottom-3 left-3 rounded-md px-3 py-1.5 text-xs font-medium backdrop-blur-xs">
                  Active pitched-roof scaffold handover
                </div>
              </div>

              {/* Accreditations & Fast Spec List */}
              <div className="p-6">
                <h3 className="font-default text-foreground text-base font-bold">
                  Verified trade credentials
                </h3>
                <ul className="mt-3 space-y-2.5">
                  {accreditationHighlights.map((text, i) => <li key={text} data-index={i} className="text-muted-foreground flex items-center gap-2.5 text-sm">
                      <CheckCircle2 className="text-primary size-4 shrink-0" />
                      <span>{text}</span>
                    </li>)}
                </ul>

                {/* Direct Action Block */}
                <div className="border-border mt-6 border-t pt-5">
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button asChild className="w-full sm:w-auto">
                      <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                        Request trade quote
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full sm:w-auto">
                      <a href="tel:01344929900" className="inline-flex items-center justify-center gap-2">
                        <PhoneCall className="size-4" />
                        Call direct
                      </a>
                    </Button>
                  </div>
                  <p className="text-muted-foreground mt-3 text-xs">
                    Covering Berkshire, Surrey, Hampshire, Buckinghamshire and
                    London.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
export default function Contractors() {
  const wp_query = new WP_Query({
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  });
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  }}>
      <ContractorsContent />
    </PostsProvider>;
}